import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';

/**
 * AnimatedNumber — tick-up animation from previous value to new value over 800ms.
 * Uses easeOutCubic easing via requestAnimationFrame (no state churn).
 */
const AnimatedNumber = ({ value, format }) => {
    const spanRef = useRef(null);
    const prevValueRef = useRef(value);
    const rafRef = useRef(null);

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const formatValue = useCallback(
        (v) => (format ? format(v) : Number(v).toLocaleString()),
        [format]
    );

    useEffect(() => {
        const from = prevValueRef.current;
        const to = value;
        prevValueRef.current = value;

        if (from === to || typeof from !== 'number' || typeof to !== 'number') {
            if (spanRef.current) spanRef.current.textContent = formatValue(to);
            return;
        }

        const duration = 800;
        const start = performance.now();

        const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(progress);
            const current = from + (to - from) * eased;

            if (spanRef.current) {
                spanRef.current.textContent = formatValue(
                    Number.isInteger(from) && Number.isInteger(to)
                        ? Math.round(current)
                        : current
                );
            }

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            }
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [value, formatValue]);

    return <span ref={spanRef}>{formatValue(value)}</span>;
};

/**
 * EnergyRing — small spinning SVG ring with dashed stroke.
 */
const EnergyRing = ({ color }) => (
    <Box
        component="svg"
        width="20px"
        height="20px"
        viewBox="0 0 20 20"
        sx={{
            display: 'inline-block',
            verticalAlign: 'middle',
            mr: 0.75,
            '@keyframes spin-ring': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
            },
            animation: 'spin-ring 4s linear infinite',
        }}
    >
        <circle
            cx="10"
            cy="10"
            r="8"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeDasharray="8 4"
            strokeLinecap="round"
        />
    </Box>
);

const AppTile = ({
    appCode,
    appName,
    appColor,
    onClick,
    metrics = [],
    lastActivityTime = null,
    isActive = false,
    mousePos = null,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const tileRef = useRef(null);

    // Parallax tilt — reads from mousePos ref via RAF loop to avoid state churn
    useEffect(() => {
        if (!mousePos) return;
        let rafId;
        const update = () => {
            const pos = mousePos.current;
            if (tileRef.current && pos) {
                const rx = pos.y * 2.5;
                const ry = pos.x * -2.5;
                tileRef.current.style.transform =
                    `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;
            }
            rafId = requestAnimationFrame(update);
        };
        rafId = requestAnimationFrame(update);
        return () => cancelAnimationFrame(rafId);
    }, [mousePos]);

    const handleClick = () => {
        if (onClick) onClick();
    };

    // Format last activity timestamp
    const getActivityText = () => {
        if (!lastActivityTime) return 'NO ACTIVITY';
        const now = Date.now();
        const diff = now - lastActivityTime;
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return 'JUST NOW';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        return `${Math.floor(hours / 24)}d ago`;
    };

    return (
        <Box
            ref={tileRef}
            data-tile={appCode}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Navigate to ${appName}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                height: { xs: '160px', sm: '180px', md: '200px' },
                // Glass-morphism background
                background: 'rgba(10, 18, 16, 0.45)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: isHovered
                    ? `1px solid ${appColor}`
                    : '1px solid rgba(95, 212, 196, 0.15)',
                borderRadius: '4px',
                padding: 2,
                cursor: 'pointer',
                transition: 'transform 0.15s ease-out, border 0.3s ease, box-shadow 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                fontFamily: vortxTypography.mono,
                boxShadow: isHovered ? `0 0 20px ${appColor}40` : 'none',
                // Pulsing border glow when active
                ...(isActive && {
                    '@keyframes pulse-glow': {
                        '0%': { boxShadow: `0 0 8px ${appColor}30` },
                        '100%': { boxShadow: `0 0 20px ${appColor}50` },
                    },
                    animation: 'pulse-glow 3s ease-in-out infinite alternate',
                }),
                // Scan lines
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.1) 0px, rgba(0,0,0,0.1) 1px, transparent 1px, transparent 3px)',
                    pointerEvents: 'none',
                },
            }}
        >
            {/* Header with energy ring */}
            <Box sx={{ borderBottom: `1px solid ${appColor}40`, pb: 1, position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
                <EnergyRing color={appColor} />
                <Typography sx={{
                    color: appColor,
                    fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textShadow: `0 0 10px ${appColor}`,
                }}>
                    [{appCode}] {appName}
                </Typography>
            </Box>

            {/* Metrics with animated numbers */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75, py: 1.5, position: 'relative', zIndex: 1 }}>
                {metrics.map((metric, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: isActive ? appColor : `${appColor}60`,
                            boxShadow: isActive ? `0 0 8px ${appColor}` : 'none',
                            animation: isActive ? 'pulse 2s ease-in-out infinite' : 'none',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }} />
                        <Typography sx={{
                            color: '#c8c8c8',
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                        }}>
                            {typeof metric.value === 'number' ? (
                                <AnimatedNumber value={metric.value} format={metric.format} />
                            ) : (
                                metric.value
                            )}{' '}
                            {metric.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Footer */}
            <Box sx={{ borderTop: `1px solid ${appColor}40`, pt: 1, position: 'relative', zIndex: 1 }}>
                <Typography sx={{
                    color: '#888',
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                }}>
                    LAST: {getActivityText()}
                </Typography>
            </Box>
        </Box>
    );
};

export default AppTile;
